/**
 * The <video> half of the PlayerAdapter contract — M4c's personal lane, where the source is an
 * uploaded file streamed from `/api/media/<id>/stream` instead of a YouTube embed. Shaped exactly
 * like `youtube.ts`'s handle so `Watch.tsx` can hold either and everything downstream (engine,
 * transcript, scenes, events, marks) stays adapter-agnostic.
 *
 * The element is a guest, like the iframe (F4): its own controls stay on, nothing is drawn over
 * it, and `availableRates()` is the whole speed vocabulary. `playsinline` is load-bearing — iOS
 * Safari otherwise takes the video fullscreen the moment it plays, which kills the transcript
 * beside it.
 *
 * TIME SOURCE — do not "simplify" this to `timeupdate` or rAF. Position is reported by the SAME
 * ~10 Hz `setInterval` polling the YouTube adapter uses, because the engine's invariants
 * (auto-pause latch, loop region, seek-resync suppression window, hidden-tab correctness) were
 * tuned on that cadence and `engine.test.ts` pins them. `timeupdate` fires at an unspecified
 * 4–66 Hz — too coarse at the low end for a 40 ms auto-pause backoff, and not a fixed budget —
 * and rAF is suspended outright in a hidden tab, which is precisely the case (screen locked,
 * audio continuing) this lane exists for. The `setInterval` survives there because Chrome
 * exempts *audible* pages from background timer throttling — so the clock keeps its ~10 Hz for
 * exactly as long as something is actually playing, which is exactly as long as it matters.
 *
 * BACKGROUND AUDIO (M5 step 7b) — this file used to assume a playing <video> keeps playing when
 * the screen locks. It does not: Chrome Android pauses any element with a video track as soon as
 * the page is hidden. Only <audio> survives (the TTS bar has been proving that since M3). So on
 * hide, playback is handed to a shadow <audio> on the SAME src and handed back on unlock;
 * `background-audio.ts` owns when, this file owns how. Two consequences worth knowing:
 *   · the shadow streams the same bytes as the video — one MP4 carries both tracks, and
 *     <audio> simply decodes the AAC — so a 40-minute episode costs the same on cellular as
 *     watching it would. The bandwidth fix is ADR-0006 §2's ffmpeg audio export (and the podcast
 *     feed), still deliberately deferred; it is also the escape hatch if an OEM battery manager
 *     kills the shadow anyway.
 *   · YouTube embeds can never do this — the platform rule forbids it — so nothing about the
 *     other lane changes.
 *
 * Seeks have two producers and both matter: our own `seekTo()` reports synchronously before it
 * returns (M2's finding — an adapter that doesn't leaves the engine's internalSeek flag set, so
 * the engine swallows the NEXT real seek), and the learner dragging the element's native scrubber
 * arrives as a `seeking` event. The echo of our own seek is de-duplicated against the second so
 * one action is never reported twice.
 *
 * Media Session: background playback with lock-screen controls is the one thing this lane gives
 * that an embed cannot, so a playing video claims the shared session (`lib/media-session.ts`) and
 * hands it back when it stops — the TTS bar is the other claimant.
 */

import {
  claimMediaSession,
  releaseMediaSession,
  setMediaSessionHandlers,
  setMediaSessionMetadata,
  setMediaSessionPlaybackState,
} from '../media-session';
import {
  backgroundReduce,
  initialBackgroundState,
  type BackgroundEvent,
  type BackgroundState,
} from './background-audio';
import type { PlayerState, RateReportingAdapter } from './engine';

/** Fine speed steps are the point of this lane (the IFrame only offers 0.25 increments). */
export const HTML5_RATES = [0.5, 0.75, 1, 1.25, 1.5];

/** HTMLMediaElement.HAVE_METADATA — duration and seeking are usable from here on. */
const HAVE_METADATA = 1;
// A `seeking` event is the browser's echo of a seek we just issued; it lands on the next task,
// not synchronously. Absorb at most one, and only briefly and only near our target, so a native
// drag that happens to follow immediately is still reported.
const SEEK_ECHO_MS = 500;
const SEEK_ECHO_TOLERANCE_MS = 1000;
/** Lock-screen skip buttons, when the platform doesn't name an offset itself. */
const SEEK_STEP_MS = 10_000;
/** MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED — what an element that reports no error at all means. */
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

export interface Html5PlayerHandle {
  adapter: RateReportingAdapter;
  /** Resolves on `loadedmetadata`; rejects if the element errors before that. */
  ready: Promise<void>;
  /** The live element (for anything the adapter doesn't wrap), or null once destroyed. */
  raw(): HTMLVideoElement | null;
  destroy(): void;
}

/** The half of `document` this adapter watches — a seam, since the web tests run in node. */
export interface VisibilityHost {
  visibilityState?: string;
  addEventListener(type: string, fn: () => void): void;
  removeEventListener(type: string, fn: () => void): void;
}

export interface CreateHtml5PlayerOpts {
  /** Position polling period; default 100 ms (~10 Hz) — see the docblock. */
  pollMs?: number;
  /** Where `visibilitychange` comes from; defaults to `document` when there is one. */
  visibility?: VisibilityHost | null;
  /** Builds the shadow <audio>; defaults to `document.createElement('audio')`. */
  createShadowAudio?: () => HTMLMediaElement;
  /** Initial position, applied once metadata is known. */
  startMs?: number;
  /** `error` event → MediaError.code (1 aborted, 2 network, 3 decode, 4 src unsupported). */
  onError?: (code: number) => void;
  /** Media Session metadata — what the lock screen shows while this file plays. */
  title?: string;
}

/**
 * The DOM-free half: everything the adapter does to an element it was handed. Split out from
 * `createHtml5Player` so the tests can drive a fake element in node (vitest runs without jsdom).
 */
export function html5Adapter(
  video: HTMLVideoElement,
  opts: CreateHtml5PlayerOpts = {},
): { adapter: RateReportingAdapter; ready: Promise<void>; destroy(): void } {
  const pollMs = opts.pollMs ?? 100;
  const startMs = Math.max(opts.startMs ?? 0, 0);

  let destroyed = false;
  let interval: ReturnType<typeof setInterval> | null = null;
  let metadataSeen = false;
  /** A position we could not apply yet: before HAVE_METADATA a `currentTime` write is only the
   *  "default playback start position" and Safari drops it outright. */
  let pendingStartMs: number | null = startMs > 0 ? startMs : null;
  let echoUntil = 0;
  let echoTargetMs = 0;

  const timeCbs = new Set<(ms: number) => void>();
  const stateCbs = new Set<(s: PlayerState) => void>();
  const seekCbs = new Set<(ms: number) => void>();
  const rateCbs = new Set<(rate: number) => void>();
  const emit = <T>(set: Set<(v: T) => void>, v: T) => {
    for (const cb of Array.from(set)) cb(v);
  };

  const wall = () => performance.now();

  const listeners: Array<[string, (e: Event) => void]> = [];
  const on = (type: string, fn: (e: Event) => void) => {
    listeners.push([type, fn]);
    video.addEventListener(type, fn);
  };

  // ------------------------------------------------ background audio (step 7b)

  /** Which element is currently carrying playback. The <video> unless the screen is off. */
  let bg: BackgroundState = initialBackgroundState();
  let audio: HTMLMediaElement | null = null;
  const audioListeners: Array<[string, (e: Event) => void]> = [];

  const live = (): HTMLMediaElement => (bg.mode === 'audio' && audio ? audio : video);

  const elapsed = (el: HTMLMediaElement): number => {
    const t = el.currentTime;
    return Number.isFinite(t) ? t * 1000 : 0;
  };

  const positionMs = () => {
    if (pendingStartMs != null) return pendingStartMs; // currentTime is still 0 until we can apply it
    return elapsed(live());
  };

  const playEl = (el: HTMLMediaElement) => {
    try {
      // play() rejects when the autoplay policy refuses it; nothing here can act on that, but it
      // must not surface as an unhandled rejection either.
      const p: unknown = el.play();
      if (p && typeof (p as Promise<void>).catch === 'function') {
        void (p as Promise<void>).catch(() => {});
      }
    } catch {
      /* element not in a state to play */
    }
  };
  const playSafely = () => playEl(live());

  /**
   * Arm the "this seek was ours" window. Every position WE write has to do this, or the
   * element's `seeking` echo is reported to the engine as a learner seek — which sets
   * `suppressPauseUntil` and silently swallows the next line's auto-pause.
   */
  const armEcho = (targetMs: number) => {
    echoTargetMs = targetMs;
    echoUntil = wall() + SEEK_ECHO_MS;
  };

  const seekTo = (ms: number) => {
    const target = Math.max(ms, 0);
    const el = live();
    if (el === audio || video.readyState >= HAVE_METADATA) {
      pendingStartMs = null;
      try {
        // The seek algorithm sets the official playback position synchronously, so currentTimeMs()
        // reads the new position back immediately; only the `seeking` event is deferred.
        el.currentTime = target / 1000;
      } catch {
        /* not seekable (yet) — the element keeps playing from where it is */
      }
    } else {
      pendingStartMs = target;
    }
    armEcho(target);
    // Report our own seek synchronously, before returning: the engine sets its internalSeek flag
    // immediately before calling us and consumes it here. An adapter that only reported native
    // seeks would leave that flag set and swallow the next real one (youtube.ts does the same at
    // its emit(seekCbs, target); M2 found it the hard way).
    emit(seekCbs, target);
  };

  /**
   * Hand playback to the shadow. `bg.mode` is already 'audio' by the time this runs, so the
   * video's position has to be read off the video explicitly rather than through `live()`.
   */
  const startShadow = () => {
    if (destroyed) return;
    const src = video.currentSrc || video.src;
    if (!src) {
      bg = { ...bg, mode: 'video' }; // nothing to shadow; stay as we were
      return;
    }
    const at = elapsed(video);
    const rate = video.playbackRate || 1;
    if (!audio) {
      audio = (opts.createShadowAudio ?? (() => document.createElement('audio')))();
      // 'auto', not 'metadata': the point is to have bytes ready before the screen goes off.
      try {
        audio.preload = 'auto';
      } catch {
        /* fake or minimal element */
      }
      audio.src = src;
      // `error` and `loadedmetadata` are the shadow's alone: the <video> already has its own
      // handlers for both in the `ready` block, and routing them twice would double-report.
      for (const type of [
        'play',
        'pause',
        'ended',
        'waiting',
        'seeking',
        'ratechange',
        'error',
        'loadedmetadata',
      ]) {
        const fn = (e: Event) => onMediaEvent(audio, e.type || type);
        audioListeners.push([type, fn]);
        audio.addEventListener(type, fn);
      }
    }
    try {
      // On a brand-new element this only sets the DEFAULT PLAYBACK START POSITION; the UA
      // performs the real seek when the shadow reaches metadata, and that fires `seeking`.
      // Arm the echo for both moments (here, and again on `loadedmetadata`) so neither is
      // reported to the engine as a learner seek.
      audio.currentTime = at / 1000;
      armEcho(at);
    } catch {
      /* not seekable yet — it will start from 0 and the engine resyncs */
    }
    try {
      audio.playbackRate = rate;
    } catch {
      /* rate refused */
    }
    playEl(audio);
    try {
      video.pause(); // it is about to be paused by the browser anyway; be explicit
    } catch {
      /* already paused */
    }
    // The lock screen is now driving the audio. The claim stays 'video' — one lane, one owner —
    // and the handlers below read `live()`, so they follow without being reinstalled.
    claimSession();
    setMediaSessionPlaybackState('video', 'playing');
  };

  /** Give the stream back to the video, in sync, and drop the shadow. */
  const stopShadow = () => {
    const a = audio;
    if (!a) return;
    // Only trust the shadow's clock if it ever actually loaded. A shadow whose src 404ed (an
    // expired session, a deleted upload) sits at currentTime 0 forever, and realigning to that
    // would throw the learner back to the start of a 40-minute episode on unlock. If it never
    // got metadata, leave the video where the hand-off left it.
    const usable = a.readyState >= HAVE_METADATA;
    const at = usable ? elapsed(a) : elapsed(video);
    const wasPlaying = usable && !a.paused && !a.ended;
    // Not a seek: the audio WAS the clock, so the engine's position already tracks this instant
    // and realigning the video is bookkeeping. Absorb the element's echo so it is not reported
    // as the learner dragging the scrubber.
    armEcho(at);
    try {
      video.currentTime = at / 1000;
    } catch {
      /* not seekable */
    }
    try {
      a.pause();
    } catch {
      /* already paused */
    }
    if (wasPlaying) playEl(video);
    dropShadow();
  };

  const dropShadow = () => {
    const a = audio;
    audio = null;
    if (!a) return;
    for (const [type, fn] of audioListeners.splice(0)) a.removeEventListener(type, fn);
    try {
      a.pause();
      // Detach the source or the range requests to /api/media/<id>/stream keep flowing.
      a.removeAttribute?.('src');
      a.load?.();
    } catch {
      /* already torn down */
    }
  };

  /**
   * After a hand-off or hand-back, take playback state from the ELEMENTS rather than from what
   * the reducer believed. The two can legitimately disagree: a shadow that never loaded leaves
   * the video paused, and the reducer cannot know that. Left unreconciled, the stale `playing`
   * is what tells the engine to render ⏸ over a stopped video, and tells the NEXT lock to start
   * audio over a video the learner is looking at stopped.
   */
  const reconcile = () => {
    const isPlaying = adapterIsPlaying();
    bg = { ...bg, playing: isPlaying };
    setMediaSessionPlaybackState('video', isPlaying ? 'playing' : 'paused');
    emit(stateCbs, isPlaying ? 'playing' : 'paused');
  };

  const dispatchBg = (event: BackgroundEvent) => {
    const { state: next, action } = backgroundReduce(bg, event);
    bg = next;
    if (action === 'none') return;
    if (action === 'startAudio') startShadow();
    else stopShadow();
    reconcile();
  };

  // ------------------------------------------------ media session

  const claimSession = () => {
    claimMediaSession('video');
    // Without a title, leave whatever the platform derived from the page rather than publishing
    // a placeholder onto the lock screen.
    if (opts.title) setMediaSessionMetadata('video', { title: opts.title, artist: 'nelnela' });
    // Each closure resolves `live()` at call time, so the lock-screen transport keeps driving
    // whichever element actually holds the stream — no reinstall on hand-off, nothing stale.
    setMediaSessionHandlers('video', {
      play: () => playSafely(),
      pause: () => live().pause(),
      // Lock-screen seeks go through our seekTo, so the engine resyncs its boundary tracker
      // exactly as it does for a scrubber drag.
      seekbackward: (d) =>
        seekTo(positionMs() - (d?.seekOffset != null ? d.seekOffset * 1000 : SEEK_STEP_MS)),
      seekforward: (d) =>
        seekTo(positionMs() + (d?.seekOffset != null ? d.seekOffset * 1000 : SEEK_STEP_MS)),
      seekto: (d) => {
        if (d?.seekTime != null) seekTo(d.seekTime * 1000);
      },
    });
    setMediaSessionPlaybackState('video', 'playing');
  };

  // Hand the session back the moment we stop playing: the TTS bar is the other claimant and a
  // paused video has no business holding the lock screen.
  const releaseSession = () => {
    releaseMediaSession('video');
  };

  // ------------------------------------------------ element events

  const ready = new Promise<void>((resolve, reject) => {
    const onMetadata = () => {
      if (metadataSeen || destroyed) return;
      metadataSeen = true;
      if (pendingStartMs != null) {
        const ms = pendingStartMs;
        pendingStartMs = null;
        seekTo(ms); // reported, so the engine's boundary tracker starts at the right line
      }
      resolve();
    };
    on('loadedmetadata', onMetadata);
    on('error', () => {
      const code = video.error?.code ?? MEDIA_ERR_SRC_NOT_SUPPORTED;
      if (!metadataSeen) reject(new Error(`media error ${code}`));
      opts.onError?.(code);
    });
    // The element may already have metadata (a src that was set and loaded before we got here).
    if (video.readyState >= HAVE_METADATA) onMetadata();
  });
  // Callers that only await `ready` inside an effect may never attach a catch; keep the rejection
  // from surfacing as unhandled while still rejecting for those who do.
  ready.catch(() => {});

  /**
   * One route for both elements' events, and the rule that makes the hand-off invisible to
   * everything downstream: **an event from an element that is not live is dropped**. During the
   * shadow phase the <video> still emits `pause` — we paused it — and letting that through would
   * tell the engine the learner had stopped, which is the opposite of what is happening.
   */
  function onMediaEvent(source: HTMLMediaElement | null, type: string): void {
    if (destroyed || !source || source !== live()) return;
    switch (type) {
      case 'play':
        dispatchBg({ type: 'play', at: wall() });
        claimSession();
        emit(stateCbs, 'playing');
        return;
      case 'playing':
        emit(stateCbs, 'playing');
        return;
      case 'pause':
        // Paused is NOT released. The engine's auto-pause fires at every line boundary, so a
        // study session is mostly pauses — releasing here would tear the metadata and the
        // transport handlers off the lock screen between every line and put them back a second
        // later, which on a phone in a pocket is the feature not working. The session is held
        // while a file is loaded and given up on `ended` and in destroy(); the TTS bar takes it
        // whenever the learner starts a clip (its claim wins), so holding it while paused
        // blocks nothing.
        dispatchBg({ type: 'pause', at: wall() });
        // The hand-off turns a browser pause into continued audio; only report a stop if we
        // really did stop.
        if (!adapterIsPlaying()) {
          setMediaSessionPlaybackState('video', 'paused');
          emit(stateCbs, 'paused');
        }
        return;
      case 'ended':
        dispatchBg({ type: 'ended', at: wall() });
        releaseSession();
        emit(stateCbs, 'ended');
        return;
      case 'waiting':
        emit(stateCbs, 'buffering');
        return;
      case 'seeking': {
        // Our own seek, echoed back: reported synchronously already. Bounded by time AND by
        // distance so a drag that lands somewhere else right afterwards is still reported.
        if (
          echoUntil > 0 &&
          wall() <= echoUntil &&
          Math.abs(positionMs() - echoTargetMs) <= SEEK_ECHO_TOLERANCE_MS
        ) {
          echoUntil = 0;
          return;
        }
        emit(seekCbs, positionMs());
        return;
      }
      case 'ratechange':
        // The native menu can change speed behind the engine's back.
        emit(rateCbs, source.playbackRate || 1);
        return;
      case 'loadedmetadata':
        // The shadow just applied the default playback start position we wrote before it had
        // metadata. That deferred seek is ours; absorb its echo.
        armEcho(elapsed(source));
        return;
      case 'error': {
        // Only the shadow routes `error` here (the <video> has its own handler). A failed
        // shadow is the quiet failure of this whole feature: per the resource-selection steps
        // it fires no `pause` and leaves `paused === false`, so without this the adapter would
        // report `isPlaying()` true over silence for the rest of the walk, the engine would
        // stay stuck on 'playing', and the next lock would start another shadow.
        opts.onError?.(source.error?.code ?? MEDIA_ERR_SRC_NOT_SUPPORTED);
        bg = { ...bg, mode: 'video', playing: false };
        stopShadow(); // leaves the video where the hand-off left it (the `usable` guard)
        reconcile();
        return;
      }
    }
  }

  const adapterIsPlaying = () => {
    const el = live();
    return !el.paused && !el.ended;
  };

  for (const type of ['play', 'playing', 'pause', 'ended', 'waiting', 'seeking', 'ratechange'])
    on(type, (e: Event) => onMediaEvent(video, e.type || type));

  // The screen going off is the whole point of step 7b, and it arrives as `visibilitychange`.
  const visibility =
    opts.visibility === undefined
      ? typeof document !== 'undefined'
        ? (document as unknown as VisibilityHost)
        : null
      : opts.visibility;
  const onVisibility = () => {
    if (destroyed) return;
    const hidden = visibility?.visibilityState === 'hidden';
    dispatchBg({ type: hidden ? 'hidden' : 'visible', at: wall() });
  };
  visibility?.addEventListener('visibilitychange', onVisibility);
  // Mounted while the page was ALREADY hidden: no visibilitychange will fire to say so, and the
  // reducer would then read a lock-screen ▶ as an ordinary play and hand it to a <video> that
  // cannot run in the background.
  if (visibility?.visibilityState === 'hidden') bg = { ...bg, hiddenSince: wall() };

  interval = setInterval(() => {
    if (destroyed) return;
    emit(timeCbs, positionMs());
  }, pollMs);

  // ------------------------------------------------ adapter

  const adapter: RateReportingAdapter = {
    play: playSafely,
    pause() {
      try {
        live().pause();
      } catch {
        /* element not in a state to pause */
      }
    },
    seekTo,
    currentTimeMs: positionMs,
    durationMs() {
      const d = video.duration;
      // NaN before metadata, Infinity for a live/unknown length — neither may reach the engine.
      return Number.isFinite(d) && d > 0 ? d * 1000 : null;
    },
    isPlaying: adapterIsPlaying,
    setRate(rate) {
      // Both, always: the shadow has to start at the right speed, and the video has to still be
      // at it when the screen comes back on.
      for (const el of [video, audio]) {
        if (!el) continue;
        try {
          el.playbackRate = rate;
        } catch {
          /* rate refused by the element */
        }
      }
    },
    availableRates() {
      return HTML5_RATES.slice();
    },
    onTime(cb) {
      timeCbs.add(cb);
      return () => {
        timeCbs.delete(cb);
      };
    },
    onStateChange(cb) {
      stateCbs.add(cb);
      return () => {
        stateCbs.delete(cb);
      };
    },
    onSeek(cb) {
      seekCbs.add(cb);
      return () => {
        seekCbs.delete(cb);
      };
    },
    onRateChange(cb) {
      rateCbs.add(cb);
      return () => {
        rateCbs.delete(cb);
      };
    },
  };

  return {
    adapter,
    ready,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (interval != null) clearInterval(interval);
      interval = null;
      for (const [type, fn] of listeners.splice(0)) video.removeEventListener(type, fn);
      visibility?.removeEventListener('visibilitychange', onVisibility);
      dropShadow();
      releaseSession();
      timeCbs.clear();
      stateCbs.clear();
      seekCbs.clear();
      rateCbs.clear();
    },
  };
}

/**
 * Creates a <video> inside `host` (the same absolutely-positioned 16:9 box Watch draws for the
 * iframe) and returns a PlayerAdapter over it.
 *
 * Unlike YT.Player, the element is appended — `host` is not replaced — and destroy() takes it
 * back out, so the caller can own the host across layout changes.
 */
export function createHtml5Player(
  host: HTMLElement,
  src: string,
  opts: CreateHtml5PlayerOpts = {},
): Html5PlayerHandle {
  const video = document.createElement('video');
  video.controls = true; // the player is a guest: its own controls, never ours
  video.playsInline = true; // iOS: otherwise playback goes fullscreen and the transcript is gone
  video.preload = 'metadata'; // duration + the moov atom, not the file
  video.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;background:#000';
  video.src = src;
  host.appendChild(video);

  const { adapter, ready, destroy } = html5Adapter(video, opts);
  let gone = false;

  return {
    adapter,
    ready,
    raw() {
      return gone ? null : video;
    },
    destroy() {
      if (gone) return;
      gone = true;
      destroy();
      try {
        video.pause();
        // Drop the source before detaching: an element removed from the DOM with a src still set
        // can keep its range requests to /api/media/<id>/stream in flight.
        video.removeAttribute('src');
        video.load();
      } catch {
        /* element already torn down */
      }
      video.remove();
    },
  };
}
